import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { addToFavorites, removeFromFavorites } from '../api-client/listing'
import { getFavorites, getFavoritesKey } from '../api-client/search'
import { triggerFullIntegrationVehicleIngestion } from '../api-client/integration'
import { useAuth } from '../contexts/auth'
import { VIEWS } from '../containers/auth/modals'

export default function useFavorites() {
  const [favorites, setFavorites] = useState({})
  const [favoritesProcessing, setFavoritesProcessing] = useState({})
  const [{ isAuthenticated }, { setAuthModal }] = useAuth()
  const { mutate: mutateAddToFavorites } = useMutation(addToFavorites)
  const { mutate: mutateRemoveFromFavorites } = useMutation(removeFromFavorites)

  useQuery({
    queryKey: [getFavoritesKey],
    queryFn: getFavorites,
    enabled: isAuthenticated,
    refetchInterval: 300_000,
    onSuccess: (data) => {
      setFavorites((prev) => ({
        ...prev,
        ...data.vehicles.reduce(
          (acc, current) => ({ ...acc, [current.id]: { ...current, isFavorite: true } }),
          {},
        ),
      }))
    },
  })

  useEffect(() => {
    if (!isAuthenticated && Object.keys(favorites).length > 0) {
      setFavorites({})
    }
  }, [isAuthenticated, favorites])

  const handleToggleFavorite = useCallback(
    async (vehicleId, isFavorite) => {
      setFavoritesProcessing((prev) => ({
        ...prev,
        [vehicleId]: { ...prev[vehicleId], isProcessing: true },
      }))
      if (isFavorite) {
        setFavorites((prev) => ({
          ...prev,
          [vehicleId]: { ...prev[vehicleId], isFavorite: false },
        }))
        mutateRemoveFromFavorites(
          { vehicleId },
          {
            onSuccess: () => {
              setFavoritesProcessing((prev) => ({
                ...prev,
                [vehicleId]: { ...prev[vehicleId], isProcessing: false },
              }))
            },
          },
        )
      } else {
        setFavorites((prev) => ({
          ...prev,
          [vehicleId]: { ...prev[vehicleId], isFavorite: true },
        }))
        await triggerFullIntegrationVehicleIngestion({ vehicleId })
        mutateAddToFavorites(
          { vehicleId },
          {
            onSuccess: () => {
              setFavoritesProcessing((prev) => ({
                ...prev,
                [vehicleId]: { ...prev[vehicleId], isProcessing: false },
              }))
            },
          },
        )
      }
    },
    [mutateAddToFavorites, mutateRemoveFromFavorites],
  )

  const toggleFavorite = useCallback(
    (id, isFavorite) => {
      if (isAuthenticated) {
        if (!!favoritesProcessing[id]?.isProcessing) return
        handleToggleFavorite(id, isFavorite)
      } else setAuthModal({ open: true, view: VIEWS.Login })
    },
    [isAuthenticated, setAuthModal, handleToggleFavorite, favoritesProcessing],
  )

  return { favorites, toggleFavorite, favoritesProcessing }
}
